import React, { useContext, useState } from 'react';
import { DropdownItem, DropdownSearchSelect } from '../..';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import { ContainerTypeContext } from '../../../../../context/ContainerTypeContext';
import {
  ContainerTypeMutations,
  CreateContainerTypeResponse,
  CreateContainerTypeVariables,
} from '../../../../../graphql/containerType.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { ContainerType, ContainerTypeInput } from '../../../../../types/container_type';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';

interface ContainerTypeSearchSelectProps<T = string | ContainerType> {
  items: ContainerType[];
  value: T | undefined;
  onSelect: (value: T | undefined) => void;
}

export function ContainerTypeIdSearchSelect(props: ContainerTypeSearchSelectProps<string>) {
  const value = props.items.find(ct => ct.id === props.value);
  const onSelect = (item: ContainerType | undefined) => props.onSelect(item && item.id);

  return <ContainerTypeSearchSelect {...props} value={value} onSelect={onSelect} />;
}

export default function ContainerTypeSearchSelect({
  items,
  value,
  onSelect,
}: ContainerTypeSearchSelectProps<ContainerType>) {
  const { currentCompany } = useContext(CompanyContext);
  const { isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { containerTypes, setContainerTypes } = useContext(ContainerTypeContext);

  const [open, setOpen] = useState(false);

  const [create, { loading }] = useMutation<CreateContainerTypeResponse, CreateContainerTypeVariables>(
    ContainerTypeMutations.create,
    {
      onCompleted: response => {
        const created = response.createContainerType[0];
        setContainerTypes(new Map(containerTypes).set(created.id, created));
        onSelect(created);
        setOpen(false);
      },
    },
  );

  const handleCreate = async (name: string) => {
    await create({
      variables: { containerTypes: [new ContainerTypeInput({ companyId: currentCompany.id, name }).forCreate()] },
    });
  };

  return (
    <DropdownSearchSelect<ContainerType>
      openManager={{ state: open, setState: setOpen }}
      mandatory
      label={t(TranslationKey.containerType).singular.label}
      placeholder={t(TranslationKey.selectContainerType).singular.label}
      value={value}
      items={items}
      textBuilder={item => item.name}
      onSelect={onSelect}
      lastBuilder={({ input, selected }) => {
        if (!isAdmin) return null;
        if (!input) return null;
        if ([...containerTypes.values()].some(ct => ct.name === input)) return null;

        return (
          <DropdownItem selected={selected} onClick={async () => await handleCreate(input)}>
            <div>Create new container type: "{input}"</div>
          </DropdownItem>
        );
      }}
    />
  );
}
